import { Model } from '@models/core/base.model';
import { ChangeRequestTemplate } from '@models/change-requests/change-request-template.model';
import { Employee } from '@models/employee/employee.model';
import { ChangeRequestFieldValue } from '@models/change-requests/change-request-field-value.model';
import { ChangeRequestStatus } from '@change-requests/enums/change-request-status.enum';
import { ApprovalInstance } from '@models/approvals/approval-instance.model';
import { InstanceStatus } from '@app/enums';

export class ChangeRequest extends Model {
    protected static _type = 'changeRequests.changeRequests';
    protected static _resource = 'changeRequest/changeRequests';
    protected static _version = 'v2';
    protected static _serializeAttributes = [
        'changeRequestTemplateId',
        'requestedById',
        'subjectId',
        'status',
        'completedAt',
        'effectiveAt',
        'cancelledAt',
        'fieldValues',
    ];

    get effectiveAt(): Date {
        return this._attributes['effectiveAt'];
    }

    set effectiveAt(val: Date) {
        this._attributes['effectiveAt'] = val;
    }

    get cancelledAt(): Date {
        return this._attributes['cancelledAt'];
    }

    get changeRequestTemplateId(): number {
        return this._attributes['changeRequestTemplateId'];
    }

    set changeRequestTemplateId(val: number) {
        this._attributes['changeRequestTemplateId'] = val;
    }

    get template(): ChangeRequestTemplate {
        return this.hasOne(ChangeRequestTemplate, 'template');
    }

    get subject(): Employee {
        return this.hasOne(Employee, 'subject');
    }

    get subjectId(): number {
        return this._attributes['subjectId'];
    }

    set subjectId(val: number) {
        this._attributes['subjectId'] = val;
    }

    get requestedBy(): Employee {
        return this.hasOne(Employee, 'requestedBy');
    }

    get cancelledBy(): Employee {
        return this.hasOne(Employee, 'cancelledBy');
    }

    get status(): string {
        return this._attributes['status'];
    }

    get approvalStatus(): string {
        if (this._attributes['status'] === ChangeRequestStatus.Cancelled) {
            return this._attributes['status'];
        }
        return this.approvalInstance.status;
    }

    get fieldValues(): ChangeRequestFieldValue[] {
        return this.hasMany(ChangeRequestFieldValue, 'fieldValues');
    }

    set fieldValues(val: ChangeRequestFieldValue[]) {
        this.setMany('fieldValues', val);
    }

    get approvalInstance(): ApprovalInstance {
        return this.hasOne(ApprovalInstance, 'approvalInstance');
    }

    canBeActioned(): boolean {
        return this.status === ChangeRequestStatus.Pending && this.approvalInstance.status === InstanceStatus.Pending;
    }

    isPending(): boolean {
        return this.status === ChangeRequestStatus.Pending;
    }

    isCancelled(): boolean {
        return this.status === ChangeRequestStatus.Cancelled;
    }

    /*
     * Is complete will be true when the change request has been processed and is fully complete. This will become true
     * on the effective date of the Change Request.
     */
    isComplete(): boolean {
        return this.status === ChangeRequestStatus.Complete;
    }

    /*
     * Has the change request been closed early for whatever reason
     * e.g. cancelled, forced approved, denied, or forced denied
     */
    isResolvedEarly(): boolean {
        return (
            this.isCancelled() ||
            this.approvalInstance.isDenied() ||
            this.approvalInstance.isForcedDenied() ||
            this.approvalInstance.isForcedApproved()
        );
    }
}
